import * as React from 'react';
import { useSetRecoilState } from 'recoil';
import { useIntl } from 'react-intl';
import './bluecoinCluster.scss';
import { CmdTooltip } from '@commander-services/gui-components';
import { BluecoinClusterTooltipItem } from '../Bluecoins/types';
import { autofocusAtom } from '../OnlineMapState';
import useAnalytics from '../../../hooks/useAnalytics';
import BluecoinIcon from '../Bluecoins/BluecoinIcon';

type BluecoinClusterProps = {
  clusterId: string;
  lat: number; // lat is used by OverlayView in GoogleMapsReactMarkers package
  lng: number; // lng is used by OverlayView in GoogleMapsReactMarkers package
  expansionZoom: number;
  googleMap: google.maps.Map | undefined;
  zIndex: number; // zIndex is used by OverlayView in GoogleMapsReactMarkers package
  tooltipItems: BluecoinClusterTooltipItem[];
};

export default function BluecoinCluster(props: BluecoinClusterProps) {
  const { formatMessage: f } = useIntl();
  const setAutofocus = useSetRecoilState<boolean>(autofocusAtom);
  const [isTooltipOpen, toggleIsTooltipOpen] = React.useReducer((state: boolean) => {
    return !state;
  }, false);
  const elementRef = React.useRef<HTMLDivElement | null>(null);
  const { trackEvent } = useAnalytics();

  const handleOnClick = (_event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    if (props.googleMap) {
      trackEvent(['map', 'click', 'Click bluecoin cluster on map']);
      props.googleMap.setZoom(props.expansionZoom);
      props.googleMap.panTo({ lat: props.lat, lng: props.lng });
      setAutofocus(false);
    }
  };

  React.useEffect(() => {
    if (elementRef.current) {
      const markerElement = elementRef.current.parentElement;

      if (markerElement) {
        if (isTooltipOpen) {
          markerElement.style.zIndex = String(props.zIndex + 10000000000);
        } else {
          markerElement.style.zIndex = String(props.zIndex);
        }
      }
    }
  }, [isTooltipOpen, props.zIndex]);

  const getBluecoinsInCluster = React.useMemo(() => {
    const numberOfAllTooltipItems = props.tooltipItems.length;
    const allowedTooltipItems = props.tooltipItems.slice(0, 20);
    return (
      <div className="bluecoin-cluster-tooltip" style={{ zIndex: props.zIndex, width: '100%' }}>
        <ul>
          {allowedTooltipItems.map((item: BluecoinClusterTooltipItem, index: number) => (
            <li key={`tooltip-item--${index}`}>
              {/* <i className={`cmd-icon cmd-icon-circle ${item.status}-color`} /> */}
              <BluecoinIcon
                width={14}
                height={14}
                bodyColor={item.status === 'ACTIVE' ? '#76E4CD' : '#B2AFAF'}
                strokeColor={item.status === 'ACTIVE' ? '#76E4CD' : '#B2AFAF'}
              />
              <span className="vehicle-name">{item.name}</span>
            </li>
          ))}
        </ul>
        {numberOfAllTooltipItems > 20 && (
          <p>
            {f(
              { id: 'map.bluecoinCluster.tooltip.moreItems' },
              { items: numberOfAllTooltipItems - allowedTooltipItems.length }
            )}
          </p>
        )}
      </div>
    );
  }, [f, props.tooltipItems, props.zIndex]);

  const countBluecoinActiveStatus = props.tooltipItems.filter(
    (item) => item.status === 'ACTIVE'
  ).length;

  const handleOnHover = () => {
    toggleIsTooltipOpen();
  };

  return (
    <div
      id={`cluster-${props.clusterId}`}
      onClick={handleOnClick}
      ref={elementRef}
      style={{
        cursor: 'pointer',
        zIndex: Number(props.zIndex),
        opacity: 1,
      }}
      onMouseEnter={handleOnHover}
      onMouseLeave={handleOnHover}
    >
      <div
        className="e-map-pin-info fixed-width"
        style={{
          position: 'absolute',
          bottom: '0',
          transform: 'translate(-50%, -160%)',
        }}
      >
        {countBluecoinActiveStatus > 0 && (
          <div>
            <span className="info">{countBluecoinActiveStatus}</span>
          </div>
        )}
      </div>
      <CmdTooltip content={getBluecoinsInCluster} backgroundColor="#252526">
        {/* show number of bluecoin items in tooltip not markers in cluster, because one marker can have more bluecoins */}
        <div className="bluecoin-cluster">{props.tooltipItems.length}</div>
      </CmdTooltip>
    </div>
  );
}
